import { UserOutlined } from "@ant-design/icons"
import { useDebounceFn } from "ahooks"
import { Select, type SelectProps } from "components/Select/Select"
import { AdminUser } from "interface/user"
import { memo, useMemo, useState } from "react"
import { useAdminUsers } from "services/users"
import { DEBOUNCE_TIME } from "utils/constants"

export type ProviderOption = {
	value: AdminUser["id"]
	label: string
	email?: string
	disabled?: boolean
}

const SORT = ["fullName-ascending"]
const ROLE: AdminUser["userType"][] = ["Provider"]

/** Searches providers on the server by email, ID or name. */
export const SearchProvider = memo(function SearchProvider({
	linkedProviderIds = [],
	...props
}: SelectProps<AdminUser["id"], ProviderOption> & {
	/** Providers already in the organization, listed but not selectable. */
	linkedProviderIds?: AdminUser["id"][]
}) {
	const [query, setQuery] = useState("")
	const search = useDebounceFn(setQuery, { wait: DEBOUNCE_TIME })
	const hasQuery = query.trim().length > 0
	const { data: { result: providers = [] } = {}, isFetching } = useAdminUsers({
		sort: SORT,
		role: ROLE,
		query,
		enabled: hasQuery,
	})
	const options = useMemo<ProviderOption[]>(
		() =>
			// Lists nothing until the admin types, rather than an arbitrary first page
			(hasQuery ? providers : []).map(({ id, fullName, email }) => ({
				value: id,
				label: fullName,
				email,
				disabled: linkedProviderIds.includes(id),
			})),
		[hasQuery, linkedProviderIds, providers],
	)

	return (
		<Select<AdminUser["id"], ProviderOption>
			{...props}
			allowClear
			virtual={false}
			loading={isFetching}
			aria-label="Search providers"
			placeholder="Search by email, ID or name"
			prefix={<UserOutlined aria-hidden />}
			options={options}
			notFoundContent={hasQuery && !isFetching ? undefined : null}
			showSearch={{ filterOption: false, onSearch: search.run }}
			optionRender={optionRender}
		/>
	)
})

const optionRender = ({ data }: { data: ProviderOption }) => (
	<div className="flex flex-col">
		<span>
			{data.label}
			{data.disabled && " (already linked)"}
		</span>
		<span className="text-xs text-navy-60">{data.email}</span>
		<span className="text-xs text-navy-60">{data.value}</span>
	</div>
)
